import { KeyboardEvent, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Card, CardContent } from '@/components/ui/card';
import { CampaignCreationCardHeader } from '@/components/glitch-ui/campaigncreationcardheader';
import { businessProductsQueryOptions } from '@/lib/query-options';
import { useQuery } from '@tanstack/react-query';
import { BusinessGenerateLoading } from '../new/business-generate-loading';
import { Input } from '@/components/ui/input';
import { Product } from '@/components/glitch-ui/product';
import { motion } from 'framer-motion';

interface CampaignBusinessProductProps {
  businessUrl: string;
  businessProducts: string[];
  onNext: () => void;
  setBusinessProducts: (products: string[]) => void;
  setStepValid: (valid: boolean) => void;
}

export interface CampaignBusinessProductRef {
  approveProducts: () => boolean;
}

export const CampaignBusinessProduct = forwardRef<CampaignBusinessProductRef, CampaignBusinessProductProps>(({
  businessUrl,
  businessProducts,
  setBusinessProducts,
  setStepValid,
}, ref) => {
  const posthog = usePostHog();
  const { isLoading, data: defaultProducts, isError, error: queryError } = useQuery({
    ...businessProductsQueryOptions(businessUrl),
    enabled: businessProducts.length === 0,
  });
  const [products, setProducts] = useState<string[]>(businessProducts ?? []);
  const [product, setProduct] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Track product generation
  useEffect(() => {
    if (isLoading && businessProducts.length === 0) {
      posthog?.capture('onboarding_products_generation_started', {
        business_url: businessUrl,
      });
    }
  }, [isLoading, businessProducts.length, businessUrl, posthog]);

  useEffect(() => {
    if (isError) {
      posthog?.capture('onboarding_products_generation_failed', {
        business_url: businessUrl,
        error: queryError instanceof Error ? queryError.message : 'Unknown error',
      });
    }
  }, [isError, queryError, businessUrl, posthog]);

  const approveProducts = (): boolean => {
    setBusinessProducts(products);

    posthog?.capture('onboarding_products_approved', {
      product_count: products.length,
      business_url: businessUrl,
    });

    return true;
  };

  useImperativeHandle(ref, () => ({
    approveProducts
  }));

  // Always allow continuation - products are optional - fix infinite loop
  useEffect(() => {
    setStepValid(true);
  }, []);

  const handleRemoveProduct = (productToRemove: string) => {
    setProducts((prev) => prev.filter((item) => item !== productToRemove));

    // Track product removal
    posthog?.capture('onboarding_product_removed', {
      product_name: productToRemove,
      remaining_products: products.length - 1,
      business_url: businessUrl,
    });
  };

  useEffect(() => {
    if (defaultProducts && defaultProducts.length > 0) {
      setProducts(defaultProducts);

      // Track successful product generation
      posthog?.capture('onboarding_products_generation_completed', {
        business_url: businessUrl,
        product_count: defaultProducts.length,
      });
    }
  }, [defaultProducts, businessUrl, posthog]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && product.trim()) {
      setProducts((prev) => [...prev, product]);
      setProduct('');
      setError(null);

      // Track product addition
      posthog?.capture('onboarding_product_added', {
        product_name: product,
        total_products: products.length + 1,
        business_url: businessUrl,
      });
    }
  };

  return (
    <motion.div 
      className='campaign-goal-select flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {isLoading ? (
        <BusinessGenerateLoading 
          showBackButton={true}
          onBackClick={() => {
            // For queries, we can't cancel but we can provide feedback to parent component
            // This would typically allow the user to go back to previous step
            setStepValid(false);
          }}
          backButtonText="Cancel"
        />
      ) : (
        <Card className='border-none shadow-none'>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <CampaignCreationCardHeader
              title='Which of these is a product or service you provide?'
              subTitle='Remove any that are not a significant part of your business.'
            />
          </motion.div>
          <CardContent className='m-6 p-0 space-y-6'>
            <motion.div 
              className='flex flex-col bg-slate-100 rounded-lg max-h-[40vh] overflow-y-auto'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              {products && products.length > 0 && (
                <div className='grid grid-cols-1 gap-1 p-1 bg-slate-100 rounded-lg'>
                  {products.map((product, index) => (
                    <motion.div
                      key={`product-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    >
                      <Product product={product} onRemove={handleRemoveProduct} />
                    </motion.div>
                  ))}
                </div>
              )}
              <motion.div 
                className='relative'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Input
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className='w-full bg-transparent !ring-inset !ring-transparent border-none shadow-none'
                  placeholder='Enter Product/Service'
                />
                {error && <p className='text-sm text-destructive m-1 px-1'>{error}</p>}
              </motion.div>
            </motion.div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
});
